import { useEffect, useRef, useState } from "react";
import { Upload, Link2 } from "lucide-react";
import { toast } from "sonner";
import { web, errMsg } from "../../lib/api";
import { Btn } from "./kit";
import { Modal, Field, TextInput, Chips } from "./Form";

export const VISIBILITY = [["team", "Whole team"], ["players", "Players & coaches"], ["coaches", "Coaches only"]];
const ACCEPT = "image/*,application/pdf,video/mp4,video/quicktime,video/webm,video/x-m4v";
const MAX = 500 * 1024 * 1024;

const sendFile = async (file, meta, tid, onProgress) => {
  const type = file.type || (file.name.toLowerCase().endsWith(".mov") ? "video/quicktime" : "");
  const { data: s } = await web.post(`/teams/${tid}/plays`, { ...meta, filename: file.name, size: file.size, content_type: type });
  for (let n = 0; n < s.chunks; n += 1) {
    const blob = file.slice(n * s.chunk_size, (n + 1) * s.chunk_size);
    for (let attempt = 0; ; attempt += 1) {
      try {
        await web.put(`/play-files/${s.id}/chunks/${n}`, blob, { headers: { "Content-Type": "application/octet-stream" } });
        break;
      } catch (e) {
        if (attempt >= 2 || (e?.response?.status && e.response.status < 500)) throw e;
      }
    }
    onProgress((n + 1) / s.chunks);
  }
  await web.post(`/play-files/${s.id}/complete`);
};

export const PlayUpload = ({ open, onClose, tid, category, onDone }) => {
  const input = useRef(null);
  const [f, setF] = useState({ title: "", category, visibility: "team" });
  const [file, setFile] = useState(null);
  const [source, setSource] = useState("file");
  const [url, setUrl] = useState("");
  const [progress, setProgress] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));
  useEffect(() => { if (open) setF((s) => ({ ...s, category })); }, [open, category]);
  const close = () => { if (progress === null) { setFile(null); setUrl(""); setSource("file"); setF({ title: "", category, visibility: "team" }); onClose(); } };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim()) return toast.error("Give the play a name");
    if (source === "link") {
      if (!/^https:\/\/\S+\.\S+/.test(url.trim())) return toast.error("Paste a full video link that starts with https://");
      setProgress(0);
      try {
        await web.post(`/teams/${tid}/plays/link`, { ...f, title: f.title.trim(), url: url.trim() });
        toast.success("Play posted");
        setProgress(null);
        onDone();
        close();
      } catch (err) {
        toast.error(errMsg(err));
        setProgress(null);
      }
      return;
    }
    if (!file) return toast.error("Pick a file");
    if (file.size > MAX) return toast.error("Files must be 500 MB or smaller");
    setProgress(0);
    try {
      await sendFile(file, { ...f, title: f.title.trim() }, tid, setProgress);
      toast.success("Play posted");
      setProgress(null);
      onDone();
      close();
    } catch (err) {
      toast.error(errMsg(err));
      setProgress(null);
    }
  };

  return (
    <Modal open={open} onClose={close} testid="play-upload-dialog" title="Post a play" description="A diagram (image or PDF), a video file, or a video link (YouTube, Hudl, Vimeo). Your team sees it in the GAMEDAY app too.">
      <form onSubmit={submit} className="mt-2 space-y-4">
        <Field label="Play name"><TextInput data-testid="play-title-input" value={f.title} onChange={set("title")} placeholder="Horns flare" /></Field>
        <Field label="Side of the ball"><Chips testid="play-category" options={[["offense", "Offense"], ["defense", "Defense"]]} value={f.category} onChange={set("category")} /></Field>
        <Field label="Who can see it"><Chips testid="play-visibility" options={VISIBILITY} value={f.visibility} onChange={set("visibility")} /></Field>
        <Field label="Add"><Chips testid="play-source" options={[["file", "Upload a file"], ["link", "Video link"]]} value={source} onChange={setSource} /></Field>
        {source === "link" ? (
          <Field label="Video link">
            <div className="relative">
              <Link2 className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#39FF14]" />
              <TextInput data-testid="play-link-input" type="url" inputMode="url" value={url} maxLength={1000} onChange={(e) => setUrl(e.target.value)} placeholder="https://youtu.be/..." className="pl-9" />
            </div>
          </Field>
        ) : (
        <>
        <input ref={input} type="file" accept={ACCEPT} className="hidden" data-testid="play-file-input" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
        <button type="button" data-testid="play-file-pick" onClick={() => input.current?.click()}
          className="flex w-full items-center gap-3 rounded-xl border border-dashed border-[#1E293B] bg-[#0F172A] px-4 py-4 text-left text-sm text-slate-300 transition-colors hover:border-[#39FF14]/60">
          <Upload className="h-5 w-5 text-[#39FF14]" />
          <span className="min-w-0 truncate">{file ? `${file.name} · ${(file.size / 1048576).toFixed(1)} MB` : "Choose an image, PDF or video (up to 500 MB)"}</span>
        </button>
        </>
        )}
        {progress !== null && source === "file" && (
          <div data-testid="play-upload-progress" className="h-2 overflow-hidden rounded-full bg-[#162032]"><div className="h-full bg-[#39FF14] transition-[width]" style={{ width: `${Math.round(progress * 100)}%` }} /></div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <Btn type="button" variant="ghost" onClick={close} disabled={progress !== null} data-testid="play-upload-cancel">Cancel</Btn>
          <Btn type="submit" disabled={progress !== null} data-testid="play-upload-submit">{progress !== null ? (source === "link" ? "Posting…" : `Uploading ${Math.round(progress * 100)}%`) : "Post play"}</Btn>
        </div>
      </form>
    </Modal>
  );
};
